<script setup lang="ts">
/**
 * THE MENU'S SPREADSHEET, brought into the draft.
 *
 * Specification: ../../test/menu-import-sheet.spec.ts; the reading is
 * `menu-import.ts`. What will happen is shown before anything does, and the
 * rows land in the draft: nothing sells until the menu is published.
 */
import { computed, reactive, ref } from "vue";
import type { ConsoleApi } from "../api";
import {
  decodeTable,
  guessColumns,
  parseTable,
  planImport,
  stationGuess,
  taxGuess,
  type ColumnMapping,
  type ImportTax,
} from "../menu-import";

const props = defineProps<{
  api: Pick<ConsoleApi, "importMenu" | "dineInRates">;
  stations: readonly { readonly stationId: string; readonly name: string }[];
  t: (key: string) => string;
  /** Reads the chosen file; the browser's own unless a test says otherwise. */
  read?: (file: File) => Promise<Uint8Array>;
}>();

const emit = defineEmits<{ done: [created: number]; cancel: [] }>();

const TAXES: readonly ImportTax[] = ["FOOD", "NON_ALC_BEVERAGE", "ALCOHOL", "TAP_WATER", "MILK_BEVERAGE", "NON_FOOD"];
const COLUMNS = ["name", "price", "category", "vat"] as const;
const OPTIONAL = new Set<string>(["category", "vat"]);
const SHOWN_PROBLEMS = 12;

const say = (key: string, params: Record<string, string | number> = {}): string =>
  Object.entries(params).reduce((text, [k, v]) => text.replace(`{${k}}`, String(v)), props.t(key));

const rows = ref<string[][] | null>(null);
const unreadable = ref(false);
/** The chosen file's name, shown beside the control — the browser's own said it in English. */
const fileName = ref<string | null>(null);
const mapping = reactive<{ -readonly [K in keyof ColumnMapping]: ColumnMapping[K] }>({
  header: true,
  name: null,
  price: null,
  category: null,
  vat: null,
});
const stationBy = reactive<Record<string, string>>({});
const taxBy = reactive<Record<string, ImportTax>>({});
const busy = ref(false);
const failure = ref<string | null>(null);
/** What the law charges sitting down, per tax; empty until asked, and if it cannot be. */
const ratesBp = ref<ReadonlyMap<ImportTax, number>>(new Map());
const result = ref<{ created: number; skipped: number } | null>(null);

const choose = async (event: Event): Promise<void> => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  fileName.value = file.name;
  const bytes = await (props.read ?? (async (f: File) => new Uint8Array(await f.arrayBuffer())))(file);
  const table = parseTable(decodeTable(bytes));
  unreadable.value = table.length === 0;
  rows.value = table.length === 0 ? null : table;
  if (table.length > 0) Object.assign(mapping, guessColumns(table[0]!, table.slice(1, 6)));
  result.value = null;
  failure.value = null;
  try {
    ratesBp.value = new Map(Object.entries(await props.api.dineInRates(TAXES)) as [ImportTax, number][]);
  } catch {
    // Without the law's rates nothing is compared; the import itself still works.
  }
};

const columnCount = computed(() => Math.max(0, ...(rows.value ?? []).map((r) => r.length)));
const columnLabel = (i: number): string => {
  const table = rows.value ?? [];
  const heading = mapping.header ? table[0]?.[i] : "";
  const example = table[mapping.header ? 1 : 0]?.[i] ?? "";
  return `${heading || say("menuImport.columnN", { n: i + 1 })}${example ? ` — ${example}` : ""}`;
};
const columnValue = (field: (typeof COLUMNS)[number]): string => {
  const at = mapping[field];
  return at === null ? "" : String(at);
};
const setColumn = (field: (typeof COLUMNS)[number], value: string): void => {
  mapping[field] = value === "" ? null : Number(value);
};

const stationIds = computed(() => props.stations.map((s) => s.stationId));

/** Every category the rows name, "" for rows with none, in the file's order. */
const keys = computed<string[]>(() => {
  const table = rows.value ?? [];
  const found: string[] = [];
  for (let r = mapping.header ? 1 : 0; r < table.length; r++) {
    const row = table[r]!;
    if (!(mapping.name === null ? "" : (row[mapping.name] ?? "").trim())) continue;
    const key = mapping.category === null ? "" : (row[mapping.category] ?? "").trim();
    if (!found.includes(key)) found.push(key);
  }
  return found;
});

const stationOf = (key: string): string => stationBy[key] ?? stationGuess(key || null, stationIds.value);
const taxOf = (key: string): ImportTax => taxBy[key] ?? taxGuess(key || null);

const plan = computed(() => {
  if (!rows.value || mapping.name === null || mapping.price === null) return null;
  return planImport(rows.value, mapping, {
    stations: new Map(keys.value.map((k) => [k, stationOf(k)])),
    taxes: new Map(keys.value.map((k) => [k, taxOf(k)])),
    ratesBp: ratesBp.value,
  });
});

const countIn = (key: string): number => plan.value?.items.filter((i) => (i.category ?? "") === key).length ?? 0;

const add = async (): Promise<void> => {
  const items = plan.value?.items ?? [];
  if (items.length === 0 || busy.value) return;
  busy.value = true;
  failure.value = null;
  try {
    const done = await props.api.importMenu(items);
    result.value = { created: done.created, skipped: done.skipped.length };
  } catch (error) {
    failure.value = say("menuImport.failed", { detail: (error as Error).message });
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <aside class="gf-drawer sheet" role="dialog" aria-modal="false" :aria-label="t('menuImport.title')">
    <button type="button" class="gf-drawer__close" :aria-label="t('menu.cancel')" @click="emit('cancel')">×</button>
    <h2 class="gf-drawer__title">{{ t("menuImport.title") }}</h2>

    <template v-if="result">
      <p class="sheet__done" role="status">
        {{ say("menuImport.done", { created: result.created }) }}
        <template v-if="result.skipped > 0"> {{ say("menuImport.skipped", { count: result.skipped }) }}</template>
      </p>
      <p class="gf-muted">{{ t("menuImport.publishHint") }}</p>
      <div class="sheet__actions">
        <button type="button" class="gf-button gf-button--primary" data-testid="import-close" @click="emit('done', result.created)">
          {{ t("menuImport.close") }}
        </button>
      </div>
    </template>

    <div v-else class="sheet__form">
      <p class="gf-muted sheet__lead">{{ t("menuImport.lead") }}</p>
      <!--
        The browser's own file control is unstyleable and speaks the browser's
        language ("Choose file · No file chosen" on a Czech screen). The real
        input stays — keyboard, screen reader and tests all reach it — inside a
        label drawn as a button, with the chosen name beside it.
      -->
      <div class="gf-field">
        <span class="gf-label">{{ t("menuImport.file") }}</span>
        <div class="sheet__file">
          <label class="gf-button sheet__pick">
            <input
              class="sheet__fileInput"
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              :aria-label="t('menuImport.choose')"
              @change="choose"
            />
            {{ t("menuImport.choose") }}
          </label>
          <span class="sheet__fileName" :class="{ 'gf-muted': fileName === null }">
            {{ fileName ?? t("menuImport.noFile") }}
          </span>
        </div>
      </div>
      <p v-if="unreadable" class="sheet__bad" role="alert">{{ t("menuImport.unreadable") }}</p>

      <template v-if="rows">
        <label class="sheet__check">
          <input v-model="mapping.header" type="checkbox" data-field="header" />
          {{ t("menuImport.header") }}
        </label>

        <div class="sheet__pair">
          <label v-for="field in COLUMNS" :key="field" class="gf-field">
            <span class="gf-label">{{ t(`menuImport.column.${field}`) }}</span>
            <select
              class="gf-input"
              :data-field="`column-${field}`"
              :value="columnValue(field)"
              @change="setColumn(field, ($event.target as HTMLSelectElement).value)"
            >
              <option v-if="OPTIONAL.has(field)" value="">{{ t("menuImport.column.none") }}</option>
              <option v-for="i in columnCount" :key="i" :value="String(i - 1)">{{ columnLabel(i - 1) }}</option>
            </select>
          </label>
        </div>

        <table v-if="plan" class="gf-table gf-table--tight">
          <thead>
            <tr>
              <th scope="col">{{ t("menuImport.column.category") }}</th>
              <th scope="col" class="gf-num">{{ t("menuImport.items") }}</th>
              <th scope="col">{{ t("menuImport.station") }}</th>
              <th scope="col">{{ t("menuImport.tax") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in keys.filter((k) => countIn(k) > 0)" :key="key">
              <th scope="row">{{ key || t("menuImport.uncategorised") }}</th>
              <td class="gf-num">{{ countIn(key) }}</td>
              <td>
                <select
                  class="gf-input"
                  :data-field="`station-${key}`"
                  :value="stationOf(key)"
                  @change="stationBy[key] = ($event.target as HTMLSelectElement).value"
                >
                  <option v-for="s in stations" :key="s.stationId" :value="s.stationId">{{ s.name }}</option>
                </select>
              </td>
              <td>
                <select
                  class="gf-input"
                  :data-field="`tax-${key}`"
                  :value="taxOf(key)"
                  @change="taxBy[key] = ($event.target as HTMLSelectElement).value as ImportTax"
                >
                  <option v-for="code in TAXES" :key="code" :value="code">{{ t(`menu.tax.${code}`) }}</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>

        <p v-if="plan" class="sheet__hint">{{ t("menuImport.taxHint") }}</p>

        <div v-if="plan && plan.mismatches.length > 0" data-testid="import-mismatches">
          <h3 class="gf-drawer__sub">{{ t("menuImport.mismatches") }}</h3>
          <ul class="gf-list">
            <li v-for="m in plan.mismatches.slice(0, SHOWN_PROBLEMS)" :key="m.line">
              {{ say("menuImport.line", { line: m.line }) }} · {{ m.name }} ·
              {{ say("menuImport.mismatch", { file: m.fileRate, rate: m.rate }) }}
            </li>
          </ul>
          <p v-if="plan.mismatches.length > SHOWN_PROBLEMS" class="gf-muted">
            {{ say("menuImport.more", { count: plan.mismatches.length - SHOWN_PROBLEMS }) }}
          </p>
        </div>

        <div v-if="plan && plan.problems.length > 0" class="sheet__problems" data-testid="import-problems">
          <h3 class="gf-drawer__sub">{{ t("menuImport.problems") }}</h3>
          <ul class="gf-list">
            <li v-for="p in plan.problems.slice(0, SHOWN_PROBLEMS)" :key="p.line">
              {{ say("menuImport.line", { line: p.line }) }} · {{ p.name || "—" }} · {{ t(`menuImport.problem.${p.reason}`) }}
            </li>
          </ul>
          <p v-if="plan.problems.length > SHOWN_PROBLEMS" class="gf-muted">
            {{ say("menuImport.more", { count: plan.problems.length - SHOWN_PROBLEMS }) }}
          </p>
        </div>

        <p v-if="failure" class="sheet__bad" role="alert">{{ failure }}</p>

        <div class="sheet__actions">
          <button type="button" class="gf-button" @click="emit('cancel')">{{ t("menu.cancel") }}</button>
          <button
            type="button"
            class="gf-button gf-button--primary"
            data-testid="import-add"
            :disabled="busy || !plan || plan.items.length === 0"
            @click="add"
          >
            {{ busy ? t("menuImport.adding") : say("menuImport.add", { count: plan?.items.length ?? 0 }) }}
          </button>
        </div>
      </template>
    </div>
  </aside>
</template>

<style scoped>
.sheet {
  width: min(44rem, 100vw);
}

.sheet__form {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-4);
}

.sheet__file {
  display: flex;
  align-items: center;
  gap: var(--gf-space-3);
  min-width: 0;
}

.sheet__pick {
  position: relative;
  flex: none;
}

.sheet__pick:focus-within {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

/* Invisible but present: the label is what is seen, the input is what is used. */
.sheet__fileInput {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

.sheet__fileName {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sheet__lead {
  margin: 0;
  font-size: var(--gf-text-sm);
}

.sheet__pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gf-space-3);
}

.sheet__check {
  display: flex;
  align-items: center;
  gap: var(--gf-space-2);
}

.sheet__hint {
  margin: 0;
  font-size: var(--gf-text-xs);
  color: var(--gf-color-text-muted);
}

.sheet__bad {
  margin: 0;
  color: var(--gf-color-danger);
}

.sheet__done {
  margin: 0 0 var(--gf-space-2);
  font-weight: var(--gf-weight-semibold);
}

.sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gf-space-2);
  padding-top: var(--gf-space-4);
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

@media (max-width: 720px) {
  .sheet__pair {
    grid-template-columns: 1fr;
  }
}
</style>
